/**
 * Shared PDF-import stepper overlay.
 * Works in both editor (cv-builder.css loaded) and landing (no external CSS).
 *
 * Le stringhe restano italiane nelle costanti e vengono tradotte con t() al momento
 * dell'uso: così il modulo non dipende dall'ordine di registrazione del dizionario.
 */
import { t } from './i18n.js';

const STEPS = [
  { label: 'Lettura del PDF', desc: 'Stiamo aprendo il tuo documento e leggendo il contenuto', icon: 'ph-file-pdf' },
  { label: 'Analisi delle sezioni', desc: 'Identifichiamo le diverse aree del tuo CV', icon: 'ph-magnifying-glass' },
  { label: 'Estrazione delle informazioni', desc: 'Recuperiamo esperienze, competenze e formazione', icon: 'ph-puzzle-piece' },
  { label: 'Organizzazione dei dati', desc: 'Mettiamo tutto in ordine nel formato giusto', icon: 'ph-tree-structure' },
  { label: 'Quasi fatto', desc: 'Ultimi ritocchi prima di mostrarti il risultato', icon: 'ph-sparkle' }
];

const DELAYS = [0, 2000, 7000, 15000, 25000];

const STYLE_ID = 'cv-pdf-stepper-style';

const CSS = `
.cv-pdf-stepper-overlay{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:1300;animation:cvStepFadeIn .3s ease}
.cv-pdf-stepper-backdrop{position:absolute;inset:0;background:rgba(15,23,42,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px)}
.cv-pdf-stepper-panel{position:relative;background:#fff;border-radius:20px;padding:2.5rem 2rem 2rem;width:min(380px,88vw);box-shadow:0 20px 60px rgba(0,0,0,.18);z-index:1;text-align:center;overflow:hidden}
.cv-pdf-stepper-panel::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#214ccd,#5b7ee5,#214ccd);background-size:200% 100%;animation:cvStepShimmer 2s linear infinite}
@keyframes cvStepShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.cv-pdf-stepper-stage{display:flex;flex-direction:column;align-items:center;gap:.75rem;min-height:160px;justify-content:center;padding:.5rem 0}
.cv-pdf-stepper-icon-wrap{width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#e9efff,#dbe3f6);display:flex;align-items:center;justify-content:center;animation:cvStepBounce 1.8s ease infinite;box-shadow:0 4px 16px rgba(33,76,205,.15)}
.cv-pdf-stepper-icon-wrap i{font-size:2rem;color:#214ccd}
.cv-pdf-stepper-stage.is-success .cv-pdf-stepper-icon-wrap{background:linear-gradient(135deg,#dcfce7,#bbf7d0);animation:cvStepBounceOnce .5s ease;box-shadow:0 4px 16px rgba(22,163,74,.15)}
.cv-pdf-stepper-stage.is-success .cv-pdf-stepper-icon-wrap i{color:#16a34a}
.cv-pdf-stepper-stage.is-success .cv-pdf-stepper-desc{color:#15803d}
.cv-pdf-stepper-stage.is-error .cv-pdf-stepper-icon-wrap{background:linear-gradient(135deg,#fef2f2,#fecaca);animation:cvStepShake .4s ease;box-shadow:0 4px 16px rgba(220,38,38,.15)}
.cv-pdf-stepper-stage.is-error .cv-pdf-stepper-icon-wrap i{color:#dc2626}
.cv-pdf-stepper-text{display:flex;flex-direction:column;gap:.35rem}
.cv-pdf-stepper-label{font-size:1.05rem;font-weight:700;color:#0f172a;min-height:1.4em}
.cv-pdf-stepper-desc{font-size:.85rem;color:#64748b;line-height:1.4;min-height:1.4em;max-width:280px;margin:0 auto}
.cv-pdf-stepper-stage.is-leaving .cv-pdf-stepper-icon-wrap,.cv-pdf-stepper-stage.is-leaving .cv-pdf-stepper-text{animation:cvStepSlideOut .25s ease forwards}
.cv-pdf-stepper-stage.is-entering .cv-pdf-stepper-icon-wrap{animation:cvStepSlideIn .35s ease,cvStepBounce 1.8s ease .35s infinite}
.cv-pdf-stepper-stage.is-entering .cv-pdf-stepper-text{animation:cvStepSlideIn .35s ease}
.cv-pdf-stepper-stage.is-success.is-entering .cv-pdf-stepper-icon-wrap{animation:cvStepSlideIn .35s ease,cvStepBounceOnce .5s ease .35s}
.cv-pdf-stepper-dots{display:flex;justify-content:center;gap:8px;margin-top:1.5rem}
.cv-pdf-stepper-dot{width:8px;height:8px;border-radius:50%;background:#e2e8f0;transition:background .3s,transform .3s}
.cv-pdf-stepper-dot.is-active{background:#214ccd;transform:scale(1.3);box-shadow:0 0 0 3px rgba(33,76,205,.2)}
.cv-pdf-stepper-dot.is-done{background:#94b3f5}
.cv-pdf-stepper-error-msg{margin-top:1rem;padding:.75rem 1rem;background:#fef2f2;border-radius:10px;color:#dc2626;font-size:.9rem;line-height:1.4;text-align:left;border:1px solid #fecaca}
.cv-pdf-stepper-close{margin-top:1rem;width:100%}
@keyframes cvStepFadeIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}
@keyframes cvStepBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes cvStepBounceOnce{0%{transform:scale(.8)}50%{transform:scale(1.15)}100%{transform:scale(1)}}
@keyframes cvStepSlideOut{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(-14px)}}
@keyframes cvStepSlideIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes cvStepShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-4px)}40%{transform:translateX(4px)}60%{transform:translateX(-3px)}80%{transform:translateX(3px)}}
`;

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

function esc(str) {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

/**
 * @param {{ lockScroll?: boolean }} opts
 *   lockScroll – add cv-modal-open to html/body (editor uses this, landing doesn't)
 */
export function createPdfImportStepper({ lockScroll = false } = {}) {
  injectStyles();

  const total = STEPS.length;
  const overlay = document.createElement('div');
  overlay.className = 'cv-pdf-stepper-overlay';
  overlay.innerHTML = `
    <div class="cv-pdf-stepper-backdrop"></div>
    <div class="cv-pdf-stepper-panel">
      <div class="cv-pdf-stepper-stage">
        <div class="cv-pdf-stepper-icon-wrap">
          <i class="ph ${STEPS[0].icon}"></i>
        </div>
        <div class="cv-pdf-stepper-text">
          <div class="cv-pdf-stepper-label">${esc(t(STEPS[0].label))}</div>
          <div class="cv-pdf-stepper-desc">${esc(t(STEPS[0].desc))}</div>
        </div>
      </div>
      <div class="cv-pdf-stepper-dots">
        ${STEPS.map((_, i) => `<span class="cv-pdf-stepper-dot${i === 0 ? ' is-active' : ''}"></span>`).join('')}
      </div>
    </div>
  `;

  const stage = overlay.querySelector('.cv-pdf-stepper-stage');
  const iconWrap = overlay.querySelector('.cv-pdf-stepper-icon-wrap');
  const labelEl = overlay.querySelector('.cv-pdf-stepper-label');
  const descEl = overlay.querySelector('.cv-pdf-stepper-desc');
  const dots = overlay.querySelectorAll('.cv-pdf-stepper-dot');
  const timers = [];
  let closed = false;
  let currentStep = 0;

  function transitionTo(index) {
    if (index === currentStep || index >= total) return;
    currentStep = index;
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.classList.toggle('is-done', i < index);
    });
    stage.classList.add('is-leaving');
    setTimeout(() => {
      iconWrap.innerHTML = `<i class="ph ${STEPS[index].icon}"></i>`;
      labelEl.textContent = t(STEPS[index].label);
      descEl.textContent = t(STEPS[index].desc);
      stage.classList.remove('is-leaving');
      stage.classList.add('is-entering');
      setTimeout(() => stage.classList.remove('is-entering'), 400);
    }, 250);
  }

  return {
    open() {
      document.body.appendChild(overlay);
      if (lockScroll) {
        document.documentElement.classList.add('cv-modal-open');
        document.body.classList.add('cv-modal-open');
      }
      DELAYS.forEach((delay, i) => {
        if (i === 0) return;
        timers.push(window.setTimeout(() => transitionTo(i), delay));
      });
    },

    complete() {
      timers.forEach(clearTimeout);
      dots.forEach((dot) => { dot.classList.remove('is-active'); dot.classList.add('is-done'); });
      stage.classList.add('is-leaving');
      setTimeout(() => {
        iconWrap.innerHTML = '<i class="ph ph-check-circle"></i>';
        labelEl.textContent = t('CV importato!');
        descEl.textContent = t('Il tuo CV è pronto per essere modificato');
        stage.classList.remove('is-leaving');
        stage.classList.add('is-entering', 'is-success');
        setTimeout(() => stage.classList.remove('is-entering'), 400);
      }, 250);
      window.setTimeout(() => this.close(), 1200);
    },

    fail(errorMessage) {
      timers.forEach(clearTimeout);
      stage.classList.add('is-leaving');
      setTimeout(() => {
        iconWrap.innerHTML = '<i class="ph ph-warning-circle"></i>';
        labelEl.textContent = '';
        descEl.textContent = '';
        stage.classList.remove('is-leaving');
        stage.classList.add('is-entering', 'is-error');
        setTimeout(() => stage.classList.remove('is-entering'), 400);
        const panel = overlay.querySelector('.cv-pdf-stepper-panel');
        const errorDiv = document.createElement('div');
        errorDiv.className = 'cv-pdf-stepper-error-msg';
        errorDiv.textContent = errorMessage;
        panel.appendChild(errorDiv);
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'myButton white-button cv-pdf-stepper-close';
        closeBtn.textContent = t('Chiudi');
        closeBtn.addEventListener('click', () => this.close());
        panel.appendChild(closeBtn);
      }, 250);
    },

    close() {
      if (closed) return;
      closed = true;
      timers.forEach(clearTimeout);
      if (lockScroll) {
        document.documentElement.classList.remove('cv-modal-open');
        document.body.classList.remove('cv-modal-open');
      }
      overlay.remove();
    }
  };
}
